import React, { useState } from 'react';
import { FaFileInvoiceDollar } from 'react-icons/fa6';
import { TbReceiptTax } from 'react-icons/tb';
import {
  FiPackage,
  FiCreditCard,
  FiFileText,
  FiPercent,
  FiClock,
  FiDollarSign,
  FiHash,
} from 'react-icons/fi';
import { HiOutlineHashtag } from 'react-icons/hi';
import { formatPrice } from '@/utils/generic';
import { getDateTimeAgo } from '@/utils/dateTime';
import type { IMmsSalesProps } from '@/pages/MmsSales/mmsSales.interface';
import { MMS_SALES_ACCORDION_TABS_TITLES } from '@/constant/mmsSales/mmsSales.constants';
import {
  NoDataPlate,
  SpinnerBlock,
  Th,
  Td,
} from '@/components/organism/TableExpand/primitives';
import { SectionCard } from '@/components/organism/Cards/SectionCard';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_TEXT, APP_BORDER, APP_BG } from '@/constant/theme/colors.constant';
import { StatChip } from '@/components/elements/StatChip';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { Text } from '@/components/atoms/AppText';
import { MetaChip } from '@/components/atoms/MetaChip';
import { tid } from '@/utils/testId';
import { MMS_SALES_TEST_IDS as TID } from '@/constant/testIds/mmsSales.testIds';

// Alias for brevity
const T = MMS_SALES_ACCORDION_TABS_TITLES;

interface Props {
  record: IMmsSalesProps | null | undefined;
  headerFields?: { orderId?: string; marketId?: string; orderDtm?: string };
  loading?: boolean;
}

type TabKey = 'products' | 'payment' | 'taxes';

// ─── Shared micro helpers ─────────────────────────────────────────────────────
const RowBadge = ({ n }: { n: number }) => (
  <div
    className="w-[auto] h-[auto] px-2 py-1 rounded flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
    style={{
      background:
        'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
    }}
  >
    {n}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
export const SalesExpandedPanel = ({
  record,
  headerFields,
  loading = false,
}: Props) => {
  const [activeTab, setActiveTab] = useState<TabKey>('products');
  const order = record?.orderDetails as any;

  const orderId = headerFields?.orderId ?? record?.orderId;
  const marketId = headerFields?.marketId ?? record?.marketId;

  if (loading) {
    return (
      <SpinnerBlock
        testId={tid(TID.EXPANDED_LOADING)}
        message={ORGANISM_PROPS.SALES_EXPANDED.LOADING_MSG}
      />
    );
  }

  // ── Tab 1: Products ───────────────────────────────────────────────────────
  const renderProducts = () => {
    const products: any[] = order?.order_products ?? [];

    // API semantics:
    //   price                  = unit selling price
    //   quantity               = units sold
    //   sale_fees.fees_total   = total fees for this line
    //   sale_taxes.taxes_total = total taxes for this line
    //   item_sub_total         = line grand total = (price × qty) + fees + taxes
    const sumBase = products.reduce((s: number, p: any) => s + parseFloat(p.price ?? '0') * (p.quantity ?? 1), 0);
    const sumCost = products.reduce((s: number, p: any) => s + parseFloat(p.cost ?? '0') * (p.quantity ?? 1), 0);
    const sumFees = products.reduce((s: number, p: any) => s + parseFloat(p.sale_fees?.fees_total ?? '0'), 0);
    const sumTax = products.reduce((s: number, p: any) => s + parseFloat(p.sale_taxes?.taxes_total ?? '0'), 0);
    const sumTotal = products.reduce((s: number, p: any) => s + parseFloat(p.item_sub_total ?? '0'), 0);

    return (
      <SectionCard
        testId={tid(TID.EXPANDED_PRODUCTS_SECTION)}
        compact
        bodyClassName="p-0"
        title={T.CARD_TITLES.PURCHASED_PRODUCT_DETAILS}
        icon={<FiPackage className="h-3.5 w-3.5" />}
        badge={products.length}
      >
        {products.length === 0 ? (
          <NoDataPlate message={ORGANISM_PROPS.SALES_EXPANDED.NO_PRODUCTS} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">

              {/* ── thead ── */}
              <thead>
                <tr className={`${APP_BG.surface} border-b ${APP_BORDER.default}`}>
                  <Th className="pl-4 pr-2 w-8">{T.PRODUCTS_TABLE.COL_NUM}</Th>
                  <Th className="px-3">{T.PRODUCTS_TABLE.COL_PRODUCT}</Th>
                  <Th className="px-3 text-center w-12">{T.PRODUCTS_TABLE.COL_QTY}</Th>
                  {/* <Th className="px-3 text-right w-22">{T.PRODUCTS_TABLE.COL_COST}</Th> */}
                  <Th className="px-3 text-right w-24">{T.PRODUCTS_TABLE.COL_PRICE}</Th>
                  <Th className="px-3 text-right w-24">{T.PRODUCTS_TABLE.COL_BASE}</Th>
                  <Th className="px-3 text-right w-20">{T.PRODUCTS_TABLE.COL_FEES}</Th>
                  <Th className="px-3 text-right w-20">{T.PRODUCTS_TABLE.COL_TAX}</Th>
                  <Th className="pl-3 pr-4 text-right w-24" style={{ color: 'var(--color-primary)' }}>{T.PRODUCTS_TABLE.COL_TOTAL}</Th>
                </tr>
              </thead>

              {/* ── tbody ── */}
              <tbody className={`divide-y ${APP_BORDER.divideSoft}`}>
                {products.map((p: any, i: number) => {
                  const qty = p.quantity ?? 1;
                  const price = parseFloat(p.price ?? '0');
                  const cost = parseFloat(p.cost ?? '0');
                  const base = price * qty;
                  const fees = parseFloat(p.sale_fees?.fees_total ?? '0');
                  const taxes = parseFloat(p.sale_taxes?.taxes_total ?? '0');
                  const total = parseFloat(p.item_sub_total ?? String(base + fees + taxes));

                  return (
                    <tr
                      key={i}
                      className="hover:bg-gray-50/70 dark:hover:bg-gray-800/20 transition-colors"
                    >
                      {/* # */}
                      <Td className="pl-4 pr-2">
                        <RowBadge n={p.product_id} />
                      </Td>

                      {/* Product */}
                      <Td className="min-w-[12rem]">
                        <p className={`text-[12px] font-semibold ${APP_TEXT.default} leading-snug`}>
                          {p.product_name ?? '—'}
                        </p>
                        {p.code && (
                          <div className="flex flex-wrap gap-x-2.5 mt-0.5">
                            <span className={`font-mono text-[10px] ${APP_TEXT.subtle}`}>
                              {T.PRODUCTS_TABLE.PRODUCT_CODE_PREFIX} {p.code}
                            </span>
                          </div>
                        )}
                      </Td>

                      {/* Qty */}
                      <Td align="center">
                        <span
                          className="inline-flex items-center justify-center min-w-[1.6rem] h-[1.6rem] px-1.5 rounded-full text-[11px] font-bold"
                          style={{ background: 'var(--color-primary)15', color: 'var(--color-primary)' }}
                        >
                          {qty}
                        </span>
                      </Td>

                      {/* Cost (procurement) */}
                      {/* <Td align="right" className={`tabular-nums text-[12px] ${APP_TEXT.subtle}`}>
                        ${formatPrice(cost)}
                      </Td> */}

                      {/* Price */}
                      <Td align="right" className={`tabular-nums text-[12px] ${APP_TEXT.soft}`}>
                        ${formatPrice(price)}
                      </Td>

                      {/* Base = price × qty */}
                      <Td align="right" className={`tabular-nums text-[12px] font-medium ${APP_TEXT.soft}`}>
                        ${formatPrice(base)}
                      </Td>

                      {/* Fees */}
                      <Td align="right" className={`tabular-nums text-[12px] ${APP_TEXT.subtle}`}>
                        ${formatPrice(fees)}
                      </Td>

                      {/* Tax */}
                      <Td align="right" className={`tabular-nums text-[12px] ${APP_TEXT.subtle}`}>
                        ${formatPrice(taxes)}
                      </Td>

                      {/* Line Total */}
                      <Td align="right" className="pl-3 pr-4 tabular-nums">
                        <span
                          className="text-[13px] font-bold"
                          style={{ color: 'var(--color-primary)' }}
                        >
                          ${formatPrice(total)}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>

              {/* ── tfoot ── */}
              <tfoot>
                <tr className={APP_BG.surface} style={{ borderTop: '1px solid var(--color-primary)25' }}>
                  {/* spans: #, Product, Qty */}
                  <Td colSpan={3} className="pl-4 py-2.5">
                    <span className={`text-[10px] font-semibold ${APP_TEXT.muted}`}>
                      {products.length} {T.PRODUCTS_TABLE.FOOTER_ITEM_LABEL}{products.length !== 1 ? 's' : ''}
                    </span>
                  </Td>

                  {/* Cost total */}
                  {/* <Td align="right" className={`py-2.5 tabular-nums text-[12px] font-semibold ${APP_TEXT.subtle}`}>
                    ${formatPrice(sumCost)}
                  </Td> */}

                  {/* Price — no footer */}
                  <Td />

                  {/* Base total */}
                  <Td align="right" className={`py-2.5 tabular-nums text-[12px] font-semibold ${APP_TEXT.soft}`}>
                    ${formatPrice(sumBase)}
                  </Td>

                  {/* Fees total */}
                  <Td align="right" className={`py-2.5 tabular-nums text-[12px] font-semibold ${APP_TEXT.subtle}`}>
                    ${formatPrice(sumFees)}
                  </Td>

                  <Td align="right" className={`py-2.5 tabular-nums text-[12px] font-semibold ${APP_TEXT.subtle}`}>
                    ${formatPrice(sumTax)}
                  </Td>

                  {/* Grand total */}
                  <Td align="right" className="pl-3 pr-4 py-2.5">
                    <span
                      className="text-[14px] font-bold tabular-nums"
                      style={{ color: 'var(--color-primary)' }}
                    >
                      ${formatPrice(sumTotal)}
                    </span>
                  </Td>
                </tr>
              </tfoot>

            </table>
          </div>
        )}
      </SectionCard>
    );
  };

  // ── Tab 2: Payment & Summary ──────────────────────────────────────────────
  const renderPayment = () => {
    const payments: any[] = order?.order_payments ?? [];
    const products: any[] = order?.order_products ?? [];

    const orderFee = parseFloat(record?.orderFee ?? '0');
    const orderTax = parseFloat(record?.orderTax ?? '0');
    const grossTotal = parseFloat(record?.orderGrossTotal ?? '0');
    const netTotal = parseFloat(record?.orderTotal ?? '0');

    // Derived so the receipt always balances: grossTotal = productsAmount + orderFee + orderTax
    const productsAmount = grossTotal - orderFee - orderTax;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-3 items-stretch">
        {/* ── Left: Payment methods ─────────────────────────────────── */}
        <SectionCard
          testId={tid(TID.EXPANDED_PAYMENTS_SECTION)}
          compact
          bodyClassName=""
          className="h-full"
          title={T.CARD_TITLES.PAYMENT_DETAILS}
          icon={<FiCreditCard className="h-3.5 w-3.5" />}
          badge={payments.length}
        >
          {payments.length === 0 ? (
            <NoDataPlate message={ORGANISM_PROPS.SALES_EXPANDED.NO_PAYMENTS} />
          ) : (
            <div className="p-3 flex flex-col gap-2">
              {payments.map((pay: any, i: number) => (
                <div
                  key={i}
                  className={`flex items-center gap-4 px-4 py-3 rounded-xl border ${APP_BORDER.soft} bg-white dark:bg-gray-800/30 hover:border-gray-200 dark:hover:border-gray-600 transition-colors`}
                >
                  {/* Payment icon badge */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white flex-shrink-0"
                    style={{
                      background:
                        'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                      boxShadow: '0 2px 8px var(--color-primary)33',
                    }}
                  >
                    <FiCreditCard className="h-4.5 w-4.5" />
                  </div>

                  {/* Type + reference */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] font-bold uppercase tracking-widest ${APP_TEXT.muted} leading-none mb-1`}>
                      {pay.type ?? '—'}
                    </p>
                    {pay.reference && (
                      <p className={`flex items-center gap-1 font-mono text-[10px] ${APP_TEXT.subtle} leading-none truncate`}>
                        <FiHash className="h-3 w-3 flex-shrink-0" />
                        {pay.reference}
                      </p>
                    )}
                  </div>

                  {/* Amount — prominent */}
                  <span
                    className="text-[18px] font-bold tabular-nums flex-shrink-0"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    ${formatPrice(pay.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ── Right: Order financial receipt ────────────────────────── */}
        <div
          className={`rounded-2xl border ${APP_BORDER.default} overflow-hidden shadow-sm`}
          data-testid={tid(TID.EXPANDED_PRICE_SECTION)}
        >
          {/* Receipt header */}
          <div className={`flex items-center gap-2.5 px-4 py-3 bg-gray-50 dark:bg-gray-800/40 border-b ${APP_BORDER.default}`}>
            <div
              className="w-6 h-6 rounded-lg flex items-center justify-center text-white flex-shrink-0"
              style={{
                background:
                  'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
              }}
            >
              <FiDollarSign className="h-3.5 w-3.5" />
            </div>
            <span className={`text-[11px] font-bold uppercase tracking-widest ${APP_TEXT.muted}`}>
              {T.SUMMARY.ORDER_SUMMARY}
            </span>
          </div>

          {/* Line items */}
          <div className={`divide-y ${APP_BORDER.divideSoft} bg-white dark:bg-gray-900/20`}>
            {/* Products subtotal */}
            <div data-testid={tid(TID.EXPANDED_RECEIPT_PRODUCTS_ROW)} className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <FiPackage className={`h-3.5 w-3.5 ${APP_TEXT.subtle} flex-shrink-0`} />
                <span className={`text-[12px] ${APP_TEXT.soft}`}>
                  {T.SUMMARY.PRODUCTS_LABEL}
                  <span className={`ml-1.5 text-[10px] ${APP_TEXT.subtle}`}>
                    ({products.length} item{products.length !== 1 ? 's' : ''})
                  </span>
                </span>
              </div>
              <span className={`text-[13px] tabular-nums font-semibold ${APP_TEXT.soft}`}>
                ${formatPrice(productsAmount)}
              </span>
            </div>

            {/* Fee */}
            <div data-testid={tid(TID.EXPANDED_RECEIPT_FEE_ROW)} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <FiFileText className={`h-3.5 w-3.5 ${APP_TEXT.faint} flex-shrink-0`} />
                <span className={`text-[12px] ${APP_TEXT.subtle}`}>
                  + {ORGANISM_PROPS.SALES_EXPANDED.LABEL_ORDER_FEE}
                </span>
              </div>
              <span className={`text-[12px] tabular-nums ${APP_TEXT.subtle}`}>
                ${formatPrice(orderFee)}
              </span>
            </div>

            {/* Tax */}
            <div data-testid={tid(TID.EXPANDED_RECEIPT_TAX_ROW)} className="flex items-center justify-between px-4 py-2.5">
              <div className="flex items-center gap-2">
                <FiPercent className={`h-3.5 w-3.5 ${APP_TEXT.faint} flex-shrink-0`} />
                <span className={`text-[12px] ${APP_TEXT.subtle}`}>
                  + {ORGANISM_PROPS.SALES_EXPANDED.LABEL_ORDER_TAX}
                </span>
              </div>
              <span className={`text-[12px] tabular-nums ${APP_TEXT.subtle}`}>
                ${formatPrice(orderTax)}
              </span>
            </div>
          </div>

          {/* Gross Total — highlighted band */}
          <div
            data-testid={tid(TID.EXPANDED_RECEIPT_GROSS_TOTAL)}
            className="flex items-center justify-between px-4 py-3.5"
            style={{
              background:
                'linear-gradient(135deg, var(--color-primary)0d, var(--color-secondary)0a)',
              borderTop: '1px solid var(--color-primary)22',
            }}
          >
            <div className="flex items-center gap-1.5">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: 'var(--color-primary)' }}
              />
              <span
                className="text-[11px] font-bold uppercase tracking-widest"
                style={{ color: 'var(--color-primary)' }}
              >
                {ORGANISM_PROPS.SALES_EXPANDED.LABEL_GROSS_TOTAL}
              </span>
            </div>
            <span
              className="text-[20px] font-bold tabular-nums"
              style={{ color: 'var(--color-primary)' }}
            >
              ${formatPrice(grossTotal)}
            </span>
          </div>

          {/* Net Total — footer note */}
          <div data-testid={tid(TID.EXPANDED_RECEIPT_NET_TOTAL)} className={`flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-t ${APP_BORDER.soft}`}>
            <span className={`text-[10px] font-bold uppercase tracking-widest ${APP_TEXT.subtle}`}>
              {T.SUMMARY.NET_TOTAL}
            </span>
            <span className={`text-[12px] tabular-nums font-semibold ${APP_TEXT.muted}`}>
              ${formatPrice(netTotal)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ── Tab 3: Taxes & Fees ───────────────────────────────────────────────────
  const renderTaxes = () => {
    const taxes: any[] = order?.order_taxes?.tax ?? [];
    const fees: any[] = order?.order_fees?.fee ?? [];

    const productTaxGroups: { product_id: string; product_name: string; taxes: any[]; taxes_total: number }[] = (
      order?.order_products ?? []
    )
      .map((p: any) => ({
        product_id: p.product_id,
        product_name: p.product_name,
        taxes: p.sale_taxes?.tax ?? [],
        taxes_total: parseFloat(p.sale_taxes?.taxes_total ?? '0'),
      }))
      .filter((p: any) => p.taxes.length > 0);

    const productFeeGroups: { product_id: string; product_name: string; fees: any[]; fees_total: number }[] = (
      order?.order_products ?? []
    )
      .map((p: any) => ({
        product_id: p.product_id,
        product_name: p.product_name,
        fees: p.sale_fees?.fee ?? [],
        fees_total: parseFloat(p.sale_fees?.fees_total ?? '0'),
      }))
      .filter((p: any) => p.fees.length > 0);

    const orderTaxTotal = parseFloat(record?.orderTax ?? '0');
    const orderFeeTotal = parseFloat(record?.orderFee ?? '0');

    const hasAnyData =
      taxes.length > 0 ||
      fees.length > 0 ||
      productTaxGroups.length > 0 ||
      productFeeGroups.length > 0;

    if (!hasAnyData) {
      return (
        <div className="px-4 py-3">
          <NoDataPlate message={ORGANISM_PROPS.SALES_EXPANDED.NO_TAXES_AND_FEES} />
        </div>
      );
    }

    // ── Shared section-header band ─────────────────────────────────────────
    const SectionBand = ({ label, count, countLabel }: { label: string; count: number; countLabel: string }) => (
      <div className={`flex items-center justify-between px-4 py-2 ${APP_BG.surface} border-b ${APP_BORDER.divideSoft}`}>
        <Text as="span" size="sm" weight="bold" uppercase tracking="widest" color="muted">{label}</Text>
        <MetaChip mono>{count} {countLabel}{count !== 1 ? 's' : ''}</MetaChip>
      </div>
    );

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">

        {/* ── TAXES card ──────────────────────────────────────────────────── */}
        <SectionCard
          testId={tid(TID.EXPANDED_TAX_SECTION)}
          compact
          bodyClassName=""
          title={T.CARD_TITLES.TAX_DETAILS}
          icon={<FiPercent className="h-3.5 w-3.5" />}
        >
          <div>
            {taxes.length === 0 && productTaxGroups.length === 0 ? (
              <div className="px-4 py-3">
                <NoDataPlate message={ORGANISM_PROPS.SALES_EXPANDED.NO_TAXES} />
              </div>
            ) : (
              <>
                {/* ─ Order taxes ─ */}
                {/* <SectionBand label={T.TAXES_FEES.SECTION_ORDER_TAXES} count={taxes.length} countLabel={T.TAXES_FEES.COUNT_LABEL_ITEM} /> */}

                {/* {taxes.length === 0 ? null : (
                  <div className={`divide-y ${APP_BORDER.divideSoft}`}>
                    {taxes.map((t: any, i: number) => (
                      <div key={i} className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <Text as="span" size="base" weight="medium" color="soft">{t.tax_name ?? '—'}</Text>
                            {t.tax_rate != null && (
                              <Text as="span" size="md" color="subtle" className="ml-2 tabular-nums">
                                {formatPrice((t.tax_rate ?? 0) * 100)}%
                              </Text>
                            )}
                          </div>
                          <Text as="span" size="lg" weight="semibold" color="default" className="tabular-nums">
                            ${formatPrice(t.tax_total)}
                          </Text>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
                          {t.tax_id != null && (
                            <Text as="span" size="md" color="subtle" mono>
                              {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_ID}: {t.tax_id}
                            </Text>
                          )}
                          {t.tax_count != null && (
                            <Text as="span" size="md" color="subtle">
                              {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_COUNT}: {t.tax_count}
                            </Text>
                          )}
                          {t.tax_value != null && (
                            <Text as="span" size="md" color="subtle" className="tabular-nums">
                              {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_VALUE}: ${formatPrice(t.tax_value)}
                            </Text>
                          )}
                          {t.included_in_price != null && (
                            <Text as="span" size="md" color="subtle">
                              {ORGANISM_PROPS.SALES_EXPANDED.LABEL_INCLUDED_IN_PRICE}: {t.included_in_price ? 'Yes' : 'No'}
                            </Text>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )} */}

                {/* ─ Per-product taxes ─ */}
                <div data-testid={tid(TID.EXPANDED_SALES_TAX_SECTION)} className={`border-t ${APP_BORDER.default}`}>
                  <SectionBand label={T.TAXES_FEES.SECTION_PER_PRODUCT} count={productTaxGroups.length} countLabel={T.TAXES_FEES.COUNT_LABEL_PRODUCT} />

                  {productTaxGroups.length === 0 ? null : (
                    <div className={`divide-y ${APP_BORDER.divideSoft}`}>
                      {productTaxGroups.map((pg, pi) => (
                        <div key={pi}>
                          <div className={`flex items-center justify-between px-4 py-2.5 ${APP_BG.surface}`}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <FiPackage className={`h-4 w-4 flex-shrink-0 ${APP_TEXT.subtle}`} />
                              <div className="min-w-0">
                                {pg.product_name && (
                                  <Text as="span" size="base" weight="semibold" color="soft" truncate className="block">
                                    {pg.product_name}
                                  </Text>
                                )}
                                <Text as="span" size="md" weight="medium" color="subtle" mono>
                                  {T.PRODUCTS_TABLE.PRODUCT_ID_PREFIX} {pg.product_id}
                                </Text>
                              </div>
                            </div>
                            <Text as="span" size="base" weight="semibold" color="default" className="tabular-nums flex-shrink-0 ml-3">
                              ${formatPrice(pg.taxes_total)}
                            </Text>
                          </div>
                          {pg.taxes.map((t: any, ti: number) => (
                            <div key={ti} className={`pl-9 pr-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors`}>
                              <div className="flex items-center justify-between">
                                <div>
                                  <Text as="span" size="base" weight="medium" color="soft">{t.tax_name ?? '—'}</Text>
                                  {t.tax_rate != null && (
                                    <Text as="span" size="md" color="subtle" className="ml-2 tabular-nums">
                                      {formatPrice((t.tax_rate ?? 0) * 100)}%
                                    </Text>
                                  )}
                                </div>
                                <Text as="span" size="base" color="muted" className="tabular-nums">
                                  ${formatPrice(t.tax_total)}
                                </Text>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                                {t.tax_id != null && (
                                  <Text as="span" size="md" color="subtle" mono>
                                    {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_ID}: {t.tax_id}
                                  </Text>
                                )}
                                {t.tax_count != null && (
                                  <Text as="span" size="md" color="subtle">
                                    {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_COUNT}: {t.tax_count}
                                  </Text>
                                )}
                                {t.tax_value != null && (
                                  <Text as="span" size="md" color="subtle" className="tabular-nums">
                                    {ORGANISM_PROPS.SALES_EXPANDED.LABEL_TAX_VALUE}: ${formatPrice(t.tax_value)}
                                  </Text>
                                )}
                                {t.included_in_price != null && (
                                  <Text as="span" size="md" color="subtle">
                                    {ORGANISM_PROPS.SALES_EXPANDED.LABEL_INCLUDED_IN_PRICE}: {t.included_in_price ? 'Yes' : 'No'}
                                  </Text>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ─ Total footer ─ */}
                <div className={`flex items-center justify-between px-4 py-3 border-t ${APP_BORDER.default} ${APP_BG.surface}`}>
                  <Text as="span" size="md" weight="bold" uppercase tracking="widest" color="muted">{T.TAXES_FEES.TOTAL_TAX}</Text>
                  <Text as="span" size="xl" weight="bold" color="primary" className="tabular-nums">
                    ${formatPrice(orderTaxTotal)}
                  </Text>
                </div>
              </>
            )}
          </div>
        </SectionCard>

        {/* ── FEES card ───────────────────────────────────────────────────── */}
        <SectionCard
          testId={tid(TID.EXPANDED_FEES_SECTION)}
          compact
          bodyClassName=""
          title={T.CARD_TITLES.SALES_FEE_DETAILS}
          icon={<FiFileText className="h-3.5 w-3.5" />}
        >
          <div>
            {fees.length === 0 && productFeeGroups.length === 0 ? (
              <div className="px-4 py-3">
                <NoDataPlate message={ORGANISM_PROPS.SALES_EXPANDED.NO_FEES} />
              </div>
            ) : (
              <>
                {/* ─ Order fees ─ */}
                {/* <SectionBand label={T.TAXES_FEES.SECTION_ORDER_FEES} count={fees.length} countLabel={T.TAXES_FEES.COUNT_LABEL_ITEM} /> */}

                {/* {fees.length === 0 ? null : (
                  <div className={`divide-y ${APP_BORDER.divideSoft}`}>
                    {fees.map((f: any, i: number) => (
                      <div key={i} className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors`}>
                        <div className="flex items-start justify-between">
                          <div className="min-w-0 flex-1">
                            <Text as="span" size="base" weight="medium" color="soft" truncate className="block">
                              {f.fee_name || '—'}
                            </Text>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                              {f.fee_id != null && (
                                <Text as="span" size="md" color="subtle" mono>
                                  {ORGANISM_PROPS.SALES_EXPANDED.LABEL_FEE_ID}: {f.fee_id}
                                </Text>
                              )}
                              {(f.fee_count != null || f.fee_value != null) && (
                                <Text as="span" size="md" color="subtle" className="tabular-nums">
                                  {f.fee_count ?? 1} × ${formatPrice(f.fee_value)}
                                </Text>
                              )}
                            </div>
                          </div>
                          <Text as="span" size="lg" weight="semibold" color="default" className="ml-4 flex-shrink-0 tabular-nums">
                            ${formatPrice(parseFloat(f.fee_total ?? '0'))}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                )} */}

                {/* ─ Per-product fees ─ */}
                <div data-testid={tid(TID.EXPANDED_SALE_FEES_SECTION)} className={`border-t ${APP_BORDER.default}`}>
                  <SectionBand label={T.TAXES_FEES.SECTION_PER_PRODUCT} count={productFeeGroups.length} countLabel={T.TAXES_FEES.COUNT_LABEL_PRODUCT} />

                  {productFeeGroups.length === 0 ? null : (
                    <div className={`divide-y ${APP_BORDER.divideSoft}`}>
                      {productFeeGroups.map((pg, pi) => (
                        <div key={pi}>
                          <div className={`flex items-center justify-between px-4 py-2.5 ${APP_BG.surface}`}>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <FiPackage className={`h-4 w-4 flex-shrink-0 ${APP_TEXT.subtle}`} />
                              <div className="min-w-0">
                                {pg.product_name && (
                                  <Text as="span" size="base" weight="semibold" color="soft" truncate className="block">
                                    {pg.product_name}
                                  </Text>
                                )}
                                <Text as="span" size="md" weight="medium" color="subtle" mono>
                                  {T.PRODUCTS_TABLE.PRODUCT_ID_PREFIX} {pg.product_id}
                                </Text>
                              </div>
                            </div>
                            <Text as="span" size="base" weight="semibold" color="default" className="tabular-nums flex-shrink-0 ml-3">
                              ${formatPrice(pg.fees_total)}
                            </Text>
                          </div>
                          {pg.fees.map((f: any, fi: number) => (
                            <div key={fi} className={`pl-9 pr-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors`}>
                              <div className="flex items-start justify-between">
                                <div className="min-w-0 flex-1">
                                  <Text as="span" size="base" weight="medium" color="soft" truncate className="block">
                                    {f.fee_name || '—'}
                                  </Text>
                                  <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                                    {f.fee_id != null && (
                                      <Text as="span" size="md" color="subtle" mono>
                                        {ORGANISM_PROPS.SALES_EXPANDED.LABEL_FEE_ID}: {f.fee_id}
                                      </Text>
                                    )}
                                    {(f.fee_count != null || f.fee_value != null) && (
                                      <Text as="span" size="md" color="subtle" className="tabular-nums">
                                        {f.fee_count ?? 1} × ${formatPrice(f.fee_value)}
                                      </Text>
                                    )}
                                  </div>
                                </div>
                                <Text as="span" size="base" color="muted" className="ml-4 flex-shrink-0 tabular-nums">
                                  ${formatPrice(parseFloat(f.fee_total ?? '0'))}
                                </Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ─ Total footer ─ */}
                <div className={`flex items-center justify-between px-4 py-3 border-t ${APP_BORDER.default} ${APP_BG.surface}`}>
                  <Text as="span" size="md" weight="bold" uppercase tracking="widest" color="muted">{T.TAXES_FEES.TOTAL_FEES}</Text>
                  <Text as="span" size="xl" weight="bold" color="primary" className="tabular-nums">
                    ${formatPrice(orderFeeTotal)}
                  </Text>
                </div>
              </>
            )}
          </div>
        </SectionCard>

      </div>
    );
  };

  const tabContent: Record<TabKey, React.ReactNode> = {
    products: renderProducts(),
    payment: renderPayment(),
    taxes: renderTaxes(),
  };

  // ── Panel root ────────────────────────────────────────────────────────────
  return (
    <div data-testid={tid(TID.EXPANDED_PANEL)} className="space-y-3">
      {/* Header: title + meta chips + tab switcher */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 mr-1">
            <div
              className="w-1 h-7 rounded-full"
              style={{
                background:
                  'linear-gradient(180deg, var(--color-primary) 0%, var(--color-tertiary) 100%)',
              }}
            />
            <Text
              as="span"
              size="sm"
              weight="bold"
              color="subtle"
              tracking="widest"
              uppercase
            >
              {T.PANEL.HEADER_TITLE}
            </Text>
          </div>

          {orderId && (
            <StatChip
              testId={tid(TID.EXPANDED_ORDER_ID_CHIP)}
              ghost
              icon={<HiOutlineHashtag className="h-3.5 w-3.5" />}
              label={T.PANEL.LABEL_ORDER_ID}
              value={orderId}
            />
          )}
          {marketId && (
            <StatChip
              testId={tid(TID.EXPANDED_MARKET_ID_CHIP)}
              ghost
              icon={<HiOutlineHashtag className="h-3.5 w-3.5" />}
              label={T.PANEL.LABEL_MARKET_ID}
              value={marketId}
            />
          )}
          {record?.updatedOn && (
            <StatChip
              testId={tid(TID.EXPANDED_ORDER_DATE_CHIP)}
              ghost
              icon={<FiClock className="h-3.5 w-3.5" />}
              label={T.PANEL.LABEL_UPDATED}
              value={getDateTimeAgo(record.updatedOn)}
            />
          )}
        </div>

        <SegmentedControl
          testId={tid(TID.EXPANDED_TAB_CONTROL)}
          size="md"
          shape="pill"
          variant="gradient"
          value={activeTab}
          onChange={(v: string) => setActiveTab(v as TabKey)}
          options={[
            {
              value: 'products',
              label: T.SECTION_TITLES.PRICE_PAYMENT_AND_PURCHASED_PRODUCTS,
              icon: FaFileInvoiceDollar,
            },
            {
              value: 'payment',
              label: T.SECTION_TITLES.PAYMENT_AND_SUMMARY,
              icon: FiCreditCard,
            },
            {
              value: 'taxes',
              label: T.SECTION_TITLES.TAXES_SALES_TAX_AND_SALES_FEE,
              icon: TbReceiptTax,
            },
          ]}
        />
      </div>

      <div>{tabContent[activeTab]}</div>
    </div>
  );
};

